import { useMemo } from 'react';
import type { FC } from 'react';

import type { SvpAssetAdditional } from '@vgtv/api-client/lib/svp_asset';
import { ComponentType } from '@vgtv/api-client/lib/component';
import type {
  AssetListComponent,
  MixedListComponent,
} from '@vgtv/api-client/lib/component/list';
import type {
  AssetSliderComponent,
  MixedSliderComponent,
  SvpEpisodeSliderComponent,
  SvpSeasonSliderComponent,
} from '@vgtv/api-client/lib/component/slider';
import type { ContinueWatchingComponent } from '@vgtv/api-client/lib/component/continue_watching';

import { getAssetProps } from '../../../../helpers/getAssetProps';
import {
  getAssetLabels,
  getAssetMainImage,
  getAssetTeaserDescription,
  getAssetVerticalImage,
} from '../../../../shared/helpers/assetHelpers';
import { getImageSources } from '../../../../helpers/images';
import type { ImageSize } from '../../../../helpers/images';
import type { ImagePreset } from '../../../../helpers/images/presets';
import { AssetVertical } from '../../../Asset/AssetVertical/AssetVertical';

type Component =
  | AssetListComponent
  | AssetSliderComponent
  | SvpSeasonSliderComponent
  | SvpEpisodeSliderComponent
  | MixedSliderComponent
  | MixedListComponent
  | ContinueWatchingComponent;

interface AssetProps {
  asset: SvpAssetAdditional;
  componentConfig?: Component;
  aspectRatio?: 'horizontal' | 'vertical';
  onClick?: () => void;
  skipMetadata?: boolean;
  size?: ImageSize;
  componentType?: Component['type'];
  onHover?: () => void;
  replacePath?: boolean;
}

const getImagePreset = (
  componentType: Component['type'],
  aspectRatio: 'horizontal' | 'vertical'
): ImagePreset => {
  switch (componentType) {
    case ComponentType.SLIDER:
    case ComponentType.CONTINUE_WATCHING:
      return aspectRatio === 'horizontal'
        ? 'assetsSlider'
        : 'assetsSliderVertical';
    case ComponentType.LIST:
      return 'assetsListVertical';
  }
};

export const AssetItem: FC<AssetProps> = ({
  asset,
  componentConfig,
  aspectRatio = 'horizontal',
  onClick,
  skipMetadata = false,
  size,
  componentType,
  onHover,
  replacePath,
}) => {
  const type = componentConfig?.type ?? componentType;

  const labels = useMemo(
    () => getAssetLabels(asset, componentConfig),
    [asset, componentConfig]
  );
  const imageSources = useMemo(
    () =>
      getImageSources({
        imageUrl:
          aspectRatio === 'vertical'
            ? getAssetVerticalImage(asset)
            : getAssetMainImage(asset),
        imagePreset: getImagePreset(type!, aspectRatio),
        aspectRatio,
        size,
      }),
    [asset, type, aspectRatio, size]
  );
  const assetProps = getAssetProps(asset);
  const description = getAssetTeaserDescription(asset);

  return (
    <AssetVertical
      {...assetProps}
      labels={labels}
      id={asset.id}
      description={description}
      imageSources={imageSources}
      onClick={onClick}
      skipMetadata={skipMetadata}
      onHover={onHover}
      replacePath={replacePath}
    />
  );
};
